import React from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import CloseIcon from "@material-ui/icons/Close";

const MAX_CHARS_DEFAULT = 2000;

// Ajuste: 8 líneas como límite visual
const MAX_LINES = 8;
// Aproximación consistente con tu UI actual
const LINE_HEIGHT_PX = 20; // ~1.45 * 12-14px
const VIEW_PADDING_Y_PX = 24; // padding vertical total (12 arriba + 12 abajo)
const MAX_VIEW_HEIGHT_PX = MAX_LINES * LINE_HEIGHT_PX + VIEW_PADDING_Y_PX;

export default function TaskCommentsEditor({
  value,
  onSave, // async (newValue) => Promise<void>
  label = "Observación",
  placeholder = "Escribí un comentario…",
  maxChars = MAX_CHARS_DEFAULT,
  readOnly = false,
  dense = false,
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value || "");
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!isEditing) setDraft(value || "");
  }, [value, isEditing]);

  const startEdit = () => {
    setError("");
    setDraft(value || "");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setError("");
    setDraft(value || "");
    setIsEditing(false);
  };

  const handleSave = async () => {
    const next = (draft || "").trim();

    if (next.length > maxChars) {
      setError(`Máximo ${maxChars} caracteres.`);
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      await onSave(next);
      setIsEditing(false);
    } catch (e) {
      setError("No se pudo guardar. Intentá nuevamente.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      {/* Header row: título + acciones */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={dense ? 0.5 : 1}
        style={{ gap: 10 }}
      >
        <Typography variant="subtitle2">{label}</Typography>

        {!readOnly && !isEditing && (
          <Tooltip title="Editar">
            <span>
              <IconButton size="small" onClick={startEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* View mode (máximo 8 líneas, luego scroll) */}
      {!isEditing && (
        <Box
          style={{
            borderRadius: 12,
            background: "#fafafa",
            border: "1px solid rgba(0,0,0,0.08)",
            padding: 12,

            maxHeight: MAX_VIEW_HEIGHT_PX,
            overflowY: "auto",
          }}
        >
          <Typography
            variant="body2"
            style={{
              whiteSpace: "pre-wrap",
              lineHeight: 1.45,
            }}
          >
            {(value || "").trim() ? value : "—"}
          </Typography>
        </Box>
      )}

      {/* Edit mode (fijo 8 líneas, scroll interno en textarea) */}
      {isEditing && (
        <Box
          style={{
            borderRadius: 12,
            background: "#fff",
            border: "1px solid rgba(0,0,0,0.12)",
            padding: 12,
          }}
        >
          <TextField
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (error) setError("");
            }}
            placeholder={placeholder}
            variant="outlined"
            fullWidth
            multiline
            rows={dense ? 6 : MAX_LINES} // en dense, un poco menos
            inputProps={{ maxLength: maxChars }}
            // Forzar scroll interno del textarea si supera las líneas visibles
            style={{}}
          />

          {/* Hack MUI: asegurar overflow del textarea */}
          <style>
            {`
              .MuiInputBase-root textarea {
                overflow-y: auto !important;
              }
            `}
          </style>

          <Box
            mt={1}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{ gap: 10 }}
          >
            <Typography
              variant="caption"
              style={{ color: error ? "#b71c1c" : "rgba(0,0,0,0.6)" }}
            >
              {error ? error : `${(draft || "").length}/${maxChars}`}
            </Typography>

            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Button
                onClick={cancelEdit}
                size="small"
                variant="text"
                startIcon={<CloseIcon />}
                disabled={isSaving}
              >
                Cancelar
              </Button>

              <Button
                onClick={handleSave}
                size="small"
                color="primary"
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                disabled={isSaving}
              >
                Guardar
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
